import { CalendarDay } from 'further-ui/utils';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { FormValues } from './FormWrapper';

export type FieldConfidence = 'high' | 'assumed' | 'unknown';

export type FieldHint = {
  confidence: FieldConfidence;
  reason?: string;
};

export type FormFillHints = Partial<Record<keyof FormValues, FieldHint>>;

type FieldFillValue<T = unknown> = {
  value: T;
  confidence: FieldConfidence;
  reason?: string;
};

export type FormFillData = {
  category?: FieldFillValue<string>;
  counterparty?: FieldFillValue<{ id?: string; name: string } | string>;
  incomeOrExpenseType?: FieldFillValue<string>;
  incomeOrExpenseState?: FieldFillValue<string>;
  fundingSource?: FieldFillValue<string>;
  currencyCode?: FieldFillValue<string>;
  amountExVatTcy?: FieldFillValue<number>;
  vatRate?: FieldFillValue<number>;
  description?: FieldFillValue<string>;
  startDate?: FieldFillValue<string>;
  apportionmentMode?: FieldFillValue<string>;
  apportionmentBasis?: FieldFillValue<string>;
  endDate?: FieldFillValue<string>;
};

// Sync handlers for fields whose onChange in InputDetailsForm resets dependent
// fields. Counterparty is excluded: the AI already provides all field values so
// calling the suggestion-sync fetch would race against and overwrite them.
export type DependentFieldHandlers = {
  onCategoryChange?: (value: string) => void;
  onIncomeOrExpenseStateChange?: (value: string) => void;
  onCurrencyCodeChange?: (value: string) => void;
};

type AiFormFillContextValue = {
  hints: FormFillHints;
  originalValues: Partial<Record<keyof FormValues, unknown>>;
  applyFormFill: (formFill: FormFillData) => void;
  clearHints: () => void;
  clearFieldHint: (field: keyof FormValues) => void;
  revertField: (field: keyof FormValues) => void;
  registerDependentFieldHandlers: (handlers: DependentFieldHandlers) => void;
  isUploadingInvoice: boolean;
  setIsUploadingInvoice: (v: boolean) => void;
  isAiScanning: boolean;
  setIsAiScanning: (v: boolean) => void;
};

const AiFormFillContext = createContext<AiFormFillContextValue | null>(null);

export const AiFormFillProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [hints, setHints] = useState<FormFillHints>({});
  const [originalValues, setOriginalValues] = useState<
    Partial<Record<keyof FormValues, unknown>>
  >({});
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [isAiScanning, setIsAiScanning] = useState(false);
  const { setValue } = useFormContext<FormValues>();
  const handlersRef = useRef<DependentFieldHandlers>({});

  const registerDependentFieldHandlers = useCallback(
    (handlers: DependentFieldHandlers) => {
      handlersRef.current = handlers;
    },
    [],
  );

  const applyFormFill = (formFill: FormFillData) => {
    const newHints: FormFillHints = {};
    const newOriginals: Partial<Record<keyof FormValues, unknown>> = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setField = (field: keyof FormValues, value: any) => {
      setValue(field as any, value, {
        shouldValidate: false,
        shouldDirty: true,
      });
      newOriginals[field] = value;
    };

    const applyFill = (
      field: keyof FormValues,
      fill: FieldFillValue | undefined,
    ) => {
      if (!fill) return;
      // Store hint for all confidence levels so the wrapper can decorate them
      newHints[field] = { confidence: fill.confidence, reason: fill.reason };
      return fill.confidence !== 'unknown';
    };

    // Handle counterparty separately: AI may return a plain string for new contacts,
    // but the form schema requires { id?: string, name: string }
    if (
      applyFill(
        'counterparty',
        formFill.counterparty as FieldFillValue | undefined,
      )
    ) {
      const raw = formFill.counterparty!.value;
      const normalised = typeof raw === 'string' ? { name: raw } : raw;
      setField('counterparty', normalised);
    }

    // Handle category before other fields: its reset clears incomeOrExpenseType
    // and incomeOrExpenseState, which are then set again below if the AI provided them.
    if (
      applyFill('category', formFill.category as FieldFillValue | undefined)
    ) {
      const value = formFill.category!.value;
      newOriginals.category = value;
      if (handlersRef.current.onCategoryChange) {
        handlersRef.current.onCategoryChange(value);
      } else {
        setValue('category', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    }

    // Handle incomeOrExpenseState before vatRate/fundingSource: when Accrued its
    // reset clears those fields, which the AI values below will then override.
    if (
      applyFill(
        'incomeOrExpenseState',
        formFill.incomeOrExpenseState as FieldFillValue | undefined,
      )
    ) {
      const value = formFill.incomeOrExpenseState!.value;
      newOriginals.incomeOrExpenseState = value;
      if (handlersRef.current.onIncomeOrExpenseStateChange) {
        handlersRef.current.onIncomeOrExpenseStateChange(value);
      } else {
        setValue('incomeOrExpenseState', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    }

    // Handle currencyCode: its reset clears apportionmentPeriods[].currencyRate.
    if (
      applyFill(
        'currencyCode',
        formFill.currencyCode as FieldFillValue | undefined,
      )
    ) {
      const value = formFill.currencyCode!.value;
      newOriginals.currencyCode = value;
      if (handlersRef.current.onCurrencyCodeChange) {
        handlersRef.current.onCurrencyCodeChange(value);
      } else {
        setValue('currencyCode', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    }

    // Simple fields with no dependent resets
    const fields: Array<
      Exclude<
        keyof FormFillData,
        | 'counterparty'
        | 'category'
        | 'incomeOrExpenseState'
        | 'currencyCode'
        | 'startDate'
        | 'endDate'
      >
    > = [
      'incomeOrExpenseType',
      'fundingSource',
      'amountExVatTcy',
      'vatRate',
      'description',
      'apportionmentMode',
      'apportionmentBasis',
    ];
    for (const field of fields) {
      const fill = formFill[field] as FieldFillValue | undefined;
      if (applyFill(field as keyof FormValues, fill)) {
        setField(field as keyof FormValues, fill!.value);
      }
    }

    // Date fields need CalendarDay conversion
    if (
      applyFill('startDate', formFill.startDate as FieldFillValue | undefined)
    ) {
      const v = formFill.startDate!.value;
      if (v) setField('startDate', new CalendarDay(v));
    }
    if (applyFill('endDate', formFill.endDate as FieldFillValue | undefined)) {
      const v = formFill.endDate!.value;
      if (v) setField('endDate', new CalendarDay(v));
    }

    setHints(newHints);
    setOriginalValues(newOriginals);
  };

  const clearHints = () => {
    setHints({});
    setOriginalValues({});
  };

  const clearFieldHint = (field: keyof FormValues) => {
    setHints((prev) => {
      const next = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete next[field];
      return next;
    });
  };

  const revertField = (field: keyof FormValues) => {
    if (!(field in originalValues)) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(field as any, originalValues[field], {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  return (
    <AiFormFillContext.Provider
      value={{
        hints,
        originalValues,
        applyFormFill,
        clearHints,
        clearFieldHint,
        revertField,
        registerDependentFieldHandlers,
        isUploadingInvoice,
        setIsUploadingInvoice,
        isAiScanning,
        setIsAiScanning,
      }}
    >
      {children}
    </AiFormFillContext.Provider>
  );
};

export const useAiFormFill = (): AiFormFillContextValue => {
  const ctx = useContext(AiFormFillContext);
  if (!ctx) {
    throw new Error('useAiFormFill must be used within AiFormFillProvider');
  }
  return ctx;
};

export const useAiFieldHint = (
  field: keyof FormValues,
): FieldHint | undefined => {
  const { hints } = useAiFormFill();
  return hints[field];
};
